<script lang="ts" setup>
import { useIntersectionObserver } from '@vueuse/core';
import { Text } from '~ds/components/ui/typography';
import { ProgressIndicator, ProgressRoot } from 'reka-ui';

import type { ProgressColor, ProgressSize } from './types';

import { progressFillColorClass, progressGlossClass, progressTrackClass } from './classList';

interface Props {
  progress?: number;
  showPercentage?: boolean;
  percentagePosition?: 'center' | 'left' | 'right';
  color?: ProgressColor;
  animate?: boolean;
  size?: ProgressSize;
  ariaLabel?: string;
}

const props = withDefaults(defineProps<Props>(), {
  progress: 0,
  showPercentage: true,
  percentagePosition: 'right',
  color: 'lime',
  animate: true,
  size: 'lg',
});

const rootEl = ref<HTMLElement | null>(null);
const inView = ref(!props.animate);

const { stop } = useIntersectionObserver(rootEl, ([entry]) => {
  if (entry?.isIntersecting) {
    inView.value = true;
    stop();
  }
}, { threshold: 0.5 });

const clamped = computed(() => Math.max(0, Math.min(100, inView.value ? props.progress : 0)));
const rounded = computed(() => Math.round(clamped.value));

// reka-ui's ProgressRoot renders `aria-label={getValueLabel(value, max)}` and
// overrides any fallthrough `aria-label` attr. To provide a custom accessible
// name we have to go through getValueLabel; undefined keeps reka-ui's percent default.
const valueLabel = computed(() =>
  props.ariaLabel === undefined ? undefined : () => props.ariaLabel as string,
);

const fillColorClass = computed(() => progressFillColorClass(props.color));
const wrapperClass = computed(() => progressTrackClass(props.size));
const glossClass = computed(() => progressGlossClass(props.size));

const percentagePositionClass = computed(() =>
  twMerge(
    cva('', {
      variants: {
        percentagePosition: {
          center: 'left-1/2 -translate-x-1/2',
          left: 'left-3',
          right: 'right-3',
        },
      },
    })({ percentagePosition: props.percentagePosition }),
  ),
);
</script>

<template>
  <ProgressRoot
    ref="rootEl"
    :model-value="clamped"
    :get-value-label="valueLabel"
    :class="wrapperClass"
  >
    <ProgressIndicator
      class="relative h-full rounded-full transition-all duration-500 ease-out"
      :class="fillColorClass"
      :style="{ width: `${clamped}%` }"
    >
      <span
        :class="glossClass"
        style="background: linear-gradient(to bottom, rgb(255 255 255 / 0.4), rgb(255 255 255 / 0.24));"
      />
    </ProgressIndicator>
    <Text
      v-if="!$slots.default && showPercentage"
      class="absolute top-1/2 -translate-y-1/2"
      size="sm"
      :class="percentagePositionClass"
    >
      {{ rounded }}%
    </Text>
    <div
      v-if="$slots.default"
      class="absolute inset-0 flex items-center justify-center pointer-events-none"
    >
      <slot />
    </div>
  </ProgressRoot>
</template>
